/**
 * These structures pertain specifically to the skill configs and extentions thereof.
 * They do NOt encompass the skill references made in the entity & bot-part configs,
 * where those entries define what skills they use and how they use them (what animations should play when the skill is cast/channelling/etc).
 */

import { EResourceType } from '../configs';
import { IBasicAssetProps } from '../configs/entity-configs-structs';
import { ERoleHatItemId } from '../enums';

// TODO: If/when skill configs are converted to TS, pull from and use the skill keys just as with "EntityDatasetKey".
export type SkillDatasetKey = string;

export enum SkillType {
  role = 'role',
  part = 'part'
}

export enum TargetStyle {

  /**
   * Self targeting is essentially non-targeting. Self-targeted skills are deployed immediately upon user input,
   * either affecting that user directly, otherwise deploying in their current position.
   */
  self = 'self',

  /**
   * Directional targeting uses a graphical field that is anchored to the caster (possibly with an offset),
   * and will begin to cast immediately upon activation, without any movement to "get in range".
   */
  directional = 'directional',

  /**
   * Point targeting uses a graphical field that is freely placed within a given set of min and max ranges.
   * Attempting to cast outside of these ranges will result in the player moving as far as
   * necessary to begin casting as soon as the appropriate range is reached.
   */
  point = 'point',

  /**
   * Entity targeting does not use a graphical field, and will alow casting on an entity with a given set of min and max ranges.
   * Attempting to cast outside of these ranges will result in the player moving as far as
   * necessary to begin casting as soon as the appropriate range is reached.
   */
  entity = 'entity'
}

export type IConfigAnimResourceStateAttrMap = { [resourceKey: string]: { [state: string]: string | number } };

/**
 * Skill entity spawn interface with loose string typing for datasetKey.
 * For type-safe entity keys, use ISkillEntitySpawn from @nova/configs instead.
 */
export interface ISkillEntitySpawnLoose {
  datasetKey: string;
  attributes?: Record<string, any>;
  stateAnimClipTimes?: IConfigAnimResourceStateAttrMap; // state name to either attribute name or just the number directly.
}

/**
 * Skill config item interface with loose typing for entitySpawn.
 * For type-safe entity spawn references, use SkillConfigItem from @nova/configs instead.
 */
export interface SkillConfigItemLoose extends IBasicAssetProps {
  // We'll go "any" type here for now so as to allow for the most flexibility while we have very few - if any - constraints on skills,
  // and to eliminate the interface over-specifying the wide array of possible value-types (valueBool, valueString, etc.)
  type: SkillType;
  attributes: Record<string, any>;
  entitySpawn?: ISkillEntitySpawnLoose[];
  debugAttrOverrides?: Record<string, any>;
}

// Internal helper
interface IconResource {
  type: EResourceType.image;
  datasetKey: string;
  stateUsage?: {
    frame?: string;
  };
}
interface AudioResource {
  type: EResourceType.audioTrack;
  datasetKey: string;
  stateUsage: unknown;
}

// * This was adapted from a structure in the REST server, which required the existence of some basic "resources"
// ! The omission of "type" here is only due to a created conflict in the REST server with an interface that's
// ! trying to inherit from both this and the "item" version of the core skills, in "read-items-config.ts".
// - The "IItem" interface also has a "type" field, not identical to this one here.
export interface ICoreSkillConfigItem extends SkillConfigItemLoose {
  description: string;
  resources: {
    vfx?: {
      type: EResourceType;
      datasetKey: string;
      stateUsage?: {
        frame?: string;
        animClips?: {
          default?: {
            clipName: string;
            loop: boolean;
          };
          onDeploy?: {
            clipName: string;
            loop: boolean;
          };
        };
      };
    };
    icon?: IconResource;
    /**
     * This really should not exist here, as there is already a mapping between the skill name and hat icon, which is in `read-items-config.ts` (REST server).
     * However, that mapping leads directly to the icon url, not it's key which maps into the image config.
     * That direct mapping would take special accommodation, which is not worth the time & effort compared to putting said mapping here.
     * TODO: Remedy this. (If this was permanent, I'd rename the above to "skillIcon", "inputIcon", or something like that, but it's not, so do not.)
     */
    hatIcon?: IconResource;
    sfxDefault?: AudioResource;
    sfx?: AudioResource;
  };
}

export interface IPartSkillConfigItem extends SkillConfigItemLoose {
  hatName?: string;
  description: string;
}
export type PartSkillsConfig = Record<string, IPartSkillConfigItem>;

export type CoreSkillConfig = Record<ERoleHatItemId, ICoreSkillConfigItem>;

export interface ISkillDeployServerSchema {
  chargesAvailable: number;
  chargesMax: number;
  cooldownCounter: number;
  cooldownTotal: number;
  chargeAntiSpamCooldownTotal?: number;
  deployRangeMin: number;
  deployRangeMax: number;
  rangeScalar: number;
  deployRange: number;
  enabled: number;
  skill: {
    constrainTargetingByDeployRange: boolean;
    datasetKey: SkillDatasetKey;
    deployFrame: boolean;
    deploymentRange: number;
    enabled: boolean;
    numTargets: number;
    skillResetFrame: boolean;
    skillSpawnEntityOffset: number;
    stateResourceData: object; // TODO
    targetingWidth: number;
    targetStyle: TargetStyle;
    type: string;
  };
  type: string;
}
